import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updatePageSEO, SEO_CONFIGS } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Update SEO
    updatePageSEO(SEO_CONFIGS.home);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold text-[#2D7A4F]">
              おかやま学習塾
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">事業内容</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">会社概要</Link>
              <Link to="/careers" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">求人情報</Link>
              <Link to="/blog" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">ブログ・新着情報</Link>
              <Link to="/trial" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-[#2D7A4F] text-white px-6 py-2.5 rounded-full hover:bg-[#236038] transition-colors flex items-center space-x-2 text-base font-medium whitespace-nowrap">
                <i className="ri-line-fill text-lg"></i>
                <span>LINE相談</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#2D7A4F] w-10 h-10 flex items-center justify-center"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-4">
              <Link to="/services" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>事業内容</Link>
              <Link to="/about" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>会社概要</Link>
              <Link to="/careers" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>求人情報</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>ブログ・新着情報</Link>
              <Link to="/trial" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="block bg-[#2D7A4F] text-white px-6 py-3 rounded-full hover:bg-[#236038] transition-colors text-center text-base font-medium">
                <i className="ri-line-fill mr-2"></i>LINE相談
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/e65cdb6eb8c508afe26ae85c6b01c3d0.jpeg" 
            alt="岡山市北区の学習塾で集中して学習する日本人学生たち"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 sm:mb-8 px-4">
              岡山市北区の<br className="sm:hidden" />
              志望校合格を<br className="sm:hidden" />実現する<br />
              おかやま学習塾
            </h1>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <Link to="/trial" className="bg-[#2D7A4F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-[#236038] transition-all text-base sm:text-lg lg:text-xl font-semibold flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl whitespace-nowrap">
                <span>無料体験授業を申し込む</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <a href="tel:0860123456" className="bg-white text-[#2D7A4F] border-2 border-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-white/90 transition-all text-base sm:text-lg lg:text-xl font-semibold flex items-center justify-center space-x-2 shadow-lg whitespace-nowrap">
                <i className="ri-phone-fill text-xl"></i>
                <span>086-012-3456</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              おかやま学習塾が<br className="sm:hidden" />選ばれる3つの理由
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <div className="group relative overflow-hidden rounded-3xl h-[500px] cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/755a9ea683591a96b34aa6872e48e8d2.jpeg" 
                alt="岡山市北区の学習塾での個別指導の様子"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">完全個別カリキュラム</h3>
                <p className="text-base lg:text-lg text-white/90 leading-relaxed">一人ひとりの学力と目標に合わせた専用プラン</p>
                <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-base text-white/80 leading-relaxed">岡山市北区の各学校の進度に合わせ、生徒様の理解度を細かく分析。苦手分野を徹底的に克服し、得意分野をさらに伸ばす完全オーダーメイドの指導を提供します。</p>
                </div>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl h-[500px] cursor-pointer md:scale-105">
              <img 
                src="https://public.readdy.ai/ai/img_res/6406ff3f0a289eb87bfc0344314bee3c.jpg" 
                alt="岡山市北区の学習塾で笑顔で学習する日本人学生"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">地域密着型指導</h3>
                <p className="text-base lg:text-lg text-white/90 mb-4 leading-relaxed">岡山市北区の学校情報を熟知した入試対策</p>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-base text-white/80 leading-relaxed">岡山市北区の小中高校の出題傾向や内申点対策を完全把握。地域に根ざした6年間の実績で、各学校に最適化された指導を実現しています。</p>
                </div>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl h-[500px] cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/05bf823a835c62599f9ad48954d693d1.jpeg" 
                alt="志望校合格を喜ぶ日本人学生"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">確かな合格実績</h3>
                <p className="text-base lg:text-lg text-white/90 leading-relaxed">過去5年間で95%の志望校合格率を達成</p>
                <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-base text-white/80 leading-relaxed">岡山市北区の主要高校への合格者多数。岡山朝日高校、岡山城東高校など、難関校への合格実績も豊富です。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#F1F8F4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12 sm:mb-16">
            <div className="inline-block bg-[#2D7A4F] text-white px-5 py-2.5 rounded-full text-base font-medium mb-6 whitespace-nowrap">
              保護者様・生徒様の声
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 px-4">
              実際に通われている<br />
              ご家族の声をご紹介
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center overflow-hidden mr-4">
                  <img src="https://readdy.ai/api/search-image?query=professional%20portrait%20of%20middle-aged%20Japanese%20woman%20mother%20with%20warm%20smile%20simple%20clean%20background&width=200&height=200&seq=testimonial001&orientation=squarish" alt="田中花子様" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">田中 花子様</div>
                  <div className="text-sm text-gray-500">中学3年生 保護者</div>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                岡山市北区で塾を探していたところ、おかやま学習塾に出会いました。個別指導のおかげで苦手だった数学が得意科目に変わり、第一志望の岡山朝日高校に合格できました！
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center overflow-hidden mr-4">
                  <img src="https://readdy.ai/api/search-image?query=professional%20portrait%20of%20Japanese%20businessman%20father%20with%20confident%20expression%20simple%20clean%20background&width=200&height=200&seq=testimonial002&orientation=squarish" alt="山田太郎様" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">山田 太郎様</div>
                  <div className="text-sm text-gray-500">高校2年生 保護者</div>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                岡山市北区の学校事情に詳しく、内申点対策も万全でした。先生方が親身になって相談に乗ってくださり、子どもも安心して通えています。
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center overflow-hidden mr-4">
                  <img src="https://readdy.ai/api/search-image?query=portrait%20of%20smiling%20Japanese%20teenage%20boy%20student%20with%20cheerful%20expression%20simple%20clean%20background&width=200&height=200&seq=testimonial003&orientation=squarish" alt="佐藤健太さん" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">佐藤 健太さん</div>
                  <div className="text-sm text-gray-500">中学3年生</div>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                自分のペースで勉強できるから、わからないところをしっかり理解できます。岡山市北区の入試情報も教えてもらえて、志望校合格に向けて頑張れています！
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center overflow-hidden mr-4">
                  <img src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/fb9cb52519496a386c38724eef967dc4.jpeg" alt="鈴木美咲様" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">鈴木 美咲様</div>
                  <div className="text-sm text-gray-500">小学6年生 保護者</div>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                中学受験対策で通い始めました。岡山市北区の中学校の情報も豊富で、子どもに合った学校選びもサポートしていただきました。無事に第一志望に合格できて感謝しています。
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center overflow-hidden mr-4">
                  <img src="https://readdy.ai/api/search-image?query=portrait%20of%20smiling%20Japanese%20teenage%20girl%20student%20with%20bright%20expression%20simple%20clean%20background&width=200&height=200&seq=testimonial005&orientation=squarish" alt="伊藤さくらさん" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">伊藤 さくらさん</div>
                  <div className="text-sm text-gray-500">高校1年生</div>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                高校受験の時からお世話になっています。岡山市北区の高校の特色も詳しく教えてもらえて、自分に合った学校を選べました。今は大学受験に向けて頑張っています！
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center overflow-hidden mr-4">
                  <img src="https://readdy.ai/api/search-image?query=professional%20portrait%20of%20Japanese%20businessman%20father%20with%20friendly%20smile%20simple%20clean%20background&width=200&height=200&seq=testimonial006&orientation=squarish" alt="高橋誠様" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">高橋 誠様</div>
                  <div className="text-sm text-gray-500">中学2年生 保護者</div>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                岡山市北区で評判の良い塾を探していて、こちらに決めました。定期テストの点数が大幅にアップし、子どもの自信にもつながっています。地域密着型の指導が魅力です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Intro Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              確かな実績で<br className="sm:hidden" />夢を叶える
            </h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl mt-4 sm:mt-6 px-4">
              岡山市北区で6年間、<br className="sm:hidden" />地域に根ざした<br className="sm:hidden" />指導を続けています
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative overflow-hidden rounded-3xl h-[450px] group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=joyful%20Japanese%20high%20school%20students%20celebrating%20together%20with%20bright%20smiles%20holding%20exam%20results%20papers%20in%20modern%20classroom%20successful%20academic%20achievement%20moment%20simple%20clean%20background%20natural%20lighting&width=800&height=900&seq=achievement-celebration-v2&orientation=portrait" 
                alt="志望校合格を喜ぶ日本人学生たち"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
              <div className="absolute inset-0 p-10 lg:p-12 flex flex-col justify-end">
                <div className="text-7xl lg:text-8xl font-bold text-white mb-4">95%</div>
                <div className="text-2xl lg:text-3xl text-white font-bold mb-3">志望校合格率</div>
                <div className="text-lg lg:text-xl text-white/90 mb-4">過去5年間の実績</div>
                <p className="text-base lg:text-lg text-white/90 leading-relaxed">
                  岡山朝日高校、岡山城東高校など難関校への合格者を多数輩出。一人ひとりの夢を現実に変えるサポートを続けています。
                </p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-3xl h-[450px] group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=group%20of%20focused%20Japanese%20students%20of%20different%20ages%20studying%20together%20in%20bright%20welcoming%20classroom%20with%20teacher%20support%20collaborative%20learning%20environment%20educational%20community%20simple%20clean%20background%20warm%20atmosphere&width=800&height=900&seq=achievement-community-v2&orientation=portrait" 
                alt="岡山市北区の学習塾で学ぶ多くの日本人学生"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
              <div className="absolute inset-0 p-10 lg:p-12 flex flex-col justify-end">
                <div className="text-7xl lg:text-8xl font-bold text-white mb-4">500+</div>
                <div className="text-2xl lg:text-3xl text-white font-bold mb-3">指導実績</div>
                <div className="text-lg lg:text-xl text-white/90 mb-4">累計指導生徒数</div>
                <p className="text-base lg:text-lg text-white/90 leading-relaxed">
                  岡山市北区の学校事情を熟知し、地域に根ざした入試対策を提供。6年間で500名以上の生徒様を志望校合格へと導いてきました。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              よくある質問
            </h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl px-4">
              岡山市北区の保護者様から<br className="sm:hidden" />よくいただくご質問
            </p>
          </div>

          <div className="space-y-6">
            <details className="bg-[#F1F8F4] rounded-2xl p-6 lg:p-8 group">
              <summary className="font-bold text-lg lg:text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>岡山市北区のどの学校の生徒が通っていますか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#2D7A4F] group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-base lg:text-lg text-gray-700 leading-relaxed">
                岡山市北区の小中高校から幅広く通塾いただいています。岡山市立岡山中央小学校、岡山市立石井中学校、岡山朝日高校、岡山城東高校など、地域の主要校の生徒様が多数在籍しています。各学校の進度や特色に合わせた指導を行っています。
              </p>
            </details>

            <details className="bg-[#F1F8F4] rounded-2xl p-6 lg:p-8 group">
              <summary className="font-bold text-lg lg:text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>個別指導と集団指導の違いは何ですか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#2D7A4F] group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-base lg:text-lg text-gray-700 leading-relaxed">
                当塾は完全個別指導を採用しています。一人ひとりの学力、目標、性格に合わせたオーダーメイドのカリキュラムで指導します。岡山市北区の各学校の進度に合わせ、苦手分野を徹底的に克服し、得意分野をさらに伸ばすことができます。
              </p>
            </details>

            <details className="bg-[#F1F8F4] rounded-2xl p-6 lg:p-8 group">
              <summary className="font-bold text-lg lg:text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>入試対策はいつから始めるべきですか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#2D7A4F] group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-base lg:text-lg text-gray-700 leading-relaxed">
                早ければ早いほど効果的ですが、いつからでも遅すぎることはありません。岡山市北区の入試傾向を熟知した講師が、現在の学力と志望校に合わせた最適なプランを提案します。中学3年生の夏からでも、効率的な学習で志望校合格を目指せます。
              </p>
            </details>

            <details className="bg-[#F1F8F4] rounded-2xl p-6 lg:p-8 group">
              <summary className="font-bold text-lg lg:text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>無料体験授業では何をしますか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#2D7A4F] group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-base lg:text-lg text-gray-700 leading-relaxed">
                まず学力診断テストを実施し、現在の理解度を把握します。その後、実際の授業を体験いただき、指導方法や教室の雰囲気を確認できます。最後に保護者様と面談し、岡山市北区の入試情報や最適な学習プランをご提案します。
              </p>
            </details>

            <details className="bg-[#F1F8F4] rounded-2xl p-6 lg:p-8 group">
              <summary className="font-bold text-lg lg:text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>授業料はどのくらいですか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-[#2D7A4F] group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-base lg:text-lg text-gray-700 leading-relaxed">
                学年や受講コマ数によって異なります。小学生は月額15,000円から、中学生は月額20,000円から、高校生は月額25,000円からとなっています。詳しくは無料体験授業の際にご説明いたします。岡山市北区で通いやすい料金設定を心がけています。
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[500px] sm:min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=bright%20modern%20Japanese%20educational%20classroom%20with%20students%20engaged%20in%20focused%20learning%20natural%20sunlight%20streaming%20through%20windows%20creating%20inspiring%20academic%20atmosphere%20clean%20minimalist%20interior%20design%20emphasizing%20growth%20and%20achievement&width=1920&height=1080&seq=cta-new002&orientation=landscape" 
            alt="岡山市北区の学習塾の温かい学習環境"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gray-500/30"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-wide drop-shadow-lg">
            まずは無料体験授業から
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white mb-3 sm:mb-4 font-medium drop-shadow-lg">
            お子様の学習状況を<br className="sm:hidden" />無料で診断
          </p>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white mb-8 sm:mb-10 font-medium drop-shadow-lg">
            最適なカリキュラムを<br className="sm:hidden" />ご提案します
          </p>
          
          <a 
            href="https://line.me/R/ti/p/@okayamajuku" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center bg-black text-white px-8 py-5 rounded-full hover:bg-gray-900 transition-all shadow-2xl text-lg lg:text-xl font-semibold whitespace-nowrap"
          >
            <span className="w-12 h-12 bg-[#FF8A65] rounded-full flex items-center justify-center mr-4 -ml-2">
              <i className="ri-line-fill text-white text-2xl"></i>
            </span>
            <span className="mr-3">LINEで無料相談</span>
            <i className="ri-arrow-right-up-line text-xl"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E3F] text-white mx-6 lg:mx-8 my-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-3xl lg:text-4xl font-light mb-6">お気軽にご相談ください</h3>
              <p className="text-base text-white/80 mb-8 leading-relaxed">
                岡山市北区で志望校合格を目指すなら、おかやま学習塾にお任せください。無料体験授業も随時受付中です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0860123456" className="text-white text-xl font-semibold hover:text-[#A5D6A7] transition-colors whitespace-nowrap flex items-center">
                  <i className="ri-phone-fill text-xl mr-2"></i>
                  <span>086-012-3456</span>
                </a>
                <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-white text-[#1B5E3F] px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-center font-semibold whitespace-nowrap">
                  <i className="ri-line-fill mr-2"></i>LINEで相談
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">サイトマップ</h4>
              <ul className="space-y-4">
                <li><Link to="/services" className="text-base hover:text-[#A5D6A7] transition-colors underline">事業内容</Link></li>
                <li><Link to="/about" className="text-base hover:text-[#A5D6A7] transition-colors underline">会社概要</Link></li>
                <li><Link to="/careers" className="text-base hover:text-[#A5D6A7] transition-colors underline">求人情報</Link></li>
                <li><Link to="/blog" className="text-base hover:text-[#A5D6A7] transition-colors underline">ブログ・新着情報</Link></li>
                <li><Link to="/trial" className="text-base hover:text-[#A5D6A7] transition-colors underline">体験申し込み</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">おかやま学習塾</h4>
              <p className="text-base text-white/80 mb-2">営業時間：15:00 〜 22:00</p>
              <p className="text-base text-white/80 mb-6">定休日：日曜日・祝日</p>
              <h4 className="text-base font-semibold mb-4 text-white/70">所在地</h4>
              <p className="text-base text-white/80">〒700-0000</p>
              <p className="text-base text-white/80">岡山市北区○○町1-2-3</p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/70 text-sm">
              © 2025 おかやま学習塾 All Rights Reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">プライバシーポリシー</a>
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">特定商取引法</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors underline">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
